import { Button, Col, Modal, ModalProps, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import { Key } from 'antd/es/table/interface';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { CheckOutlined } from '@ant-design/icons';
import { useDebounce } from '@/hooks';
import { usePlanAccounts } from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.hooks';
import {
  ListPlanAccountsQuery,
  PlanAccount,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import InfiniteTable from '../InfiniteTable';
import PlanAccountSelectedModalFilters from './PlanAccountSelectedModalFilters';
import StatusTag from '../StatusTag';

interface PlanAccountSeletedProps extends Omit<ModalProps, 'onCancel'> {
  finishCallback?: (planAccountIds?: number[]) => void;
  selectedPlanAccounIds?: number[];
  noSelection?: boolean;
  onPlanAccountSelect?: (planAccount: PlanAccount) => void;
  onCancel: VoidFunction;
  queryFilter?: ListPlanAccountsQuery;
}

export default function PlanAccountSeletedModal({
  finishCallback,
  selectedPlanAccounIds = [],
  noSelection,
  onPlanAccountSelect,
  onCancel,
  queryFilter,
  ...props
}: PlanAccountSeletedProps) {
  const intl = useIntl();
  const { Title } = Typography;
  const [queryParams, setQueryParams] = useState<ListPlanAccountsQuery>({
    order: 'ASC',
  });

  const [selectedPlanAccountKeys, setSelectedPlanAccountKeys] = useState<Key[]>(
    [],
  );

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage } = usePlanAccounts(
    { ...queryDebounce, ...queryFilter },
    props.open,
  );

  const planAccounts = flatInfiniteData(data);

  useEffect(() => {
    if (props.open) {
      setSelectedPlanAccountKeys(selectedPlanAccounIds);
    }
  }, [props.open]);

  const handleOk = (): void => {
    if (finishCallback) {
      finishCallback(selectedPlanAccountKeys as number[]);
    }

    handleCancel();
  };

  const handlePlanAccountsSelect = (selectedRowKeys: Key[]) => {
    setSelectedPlanAccountKeys(selectedRowKeys);
  };

  const handleCancel = () => {
    setQueryParams({ order: 'ASC' });

    setSelectedPlanAccountKeys([]);

    onCancel();
  };

  const columns: ColumnsType<PlanAccount> = [
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.code' }),
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.description' }),
      dataIndex: 'description',
      key: 'description',
      render: (value) => value || '-',
      ellipsis: true,
      width: 140,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.hierarchy' }),
      dataIndex: 'hierarchy',
      key: 'hierarchy',
      render: (value) =>
        intl.formatMessage({
          id: `planAccounts.modal.general.form.hierarchy.${value.toLowerCase()}`,
        }),
      width: 100,
    },
    {
      title: intl.formatMessage({ id: 'planAccounts.domain.destination' }),
      dataIndex: 'destination',
      key: 'destination',
      render: (value) =>
        intl.formatMessage({
          id: `planAccounts.modal.general.form.destination.${value.toLowerCase()}`,
        }),
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (value, row) => (
        <StatusTag
          key={row.id}
          label={value ? 'Ativa' : 'Inativa'}
          color={value ? 'green' : 'red'}
        />
      ),
      width: 100,
    },
    onPlanAccountSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onPlanAccountSelect(row)}
              icon={<CheckOutlined />}
            />
          ),
          width: '20%',
        }
      : {},
  ];

  return (
    <Modal
      title="Selecione plano de contas"
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
      destroyOnClose
      {...props}
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Busque os plano de contas</Title>
            </div>

            <PlanAccountSelectedModalFilters setQueryParams={setQueryParams} />

            <InfiniteTable<PlanAccount>
              columns={columns}
              dataSource={planAccounts}
              loading={isFetching}
              rowKey={(row) => row.id}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              rowSelection={
                !noSelection
                  ? {
                      selectedRowKeys: selectedPlanAccountKeys,
                      onChange: handlePlanAccountsSelect,
                      preserveSelectedRowKeys: true,
                    }
                  : undefined
              }
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
}
